import { QUESTION_CHILD_KEYS, getValueByPath } from "./questionTree";

function normalizeList(value) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

function normalizeValue(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function isVisible(node, parentVisible) {
  return parentVisible && (!node || node.show !== false);
}

function walkVisibleQuestionTree(
  node,
  callback,
  inheritedSection,
  parentVisible = true
) {
  const nodeVisible = isVisible(node, parentVisible);
  if (!node || !nodeVisible) return;

  const section = node.section || inheritedSection;
  callback(node, section);

  QUESTION_CHILD_KEYS.forEach((childKey) => {
    if (Array.isArray(node[childKey])) {
      node[childKey].forEach((childNode) => {
        walkVisibleQuestionTree(childNode, callback, section, nodeVisible);
      });
    }
  });
}

function questionMatchesSelector(question, section, selector) {
  if (!question || !selector) return false;

  if (
    selector.sectionNumbers &&
    (!section || !selector.sectionNumbers.includes(section.number))
  ) {
    return false;
  }

  if (
    selector.sectionNumber &&
    (!section || section.number !== selector.sectionNumber)
  ) {
    return false;
  }

  if (
    selector.sectionTitle &&
    (!section || section.title !== selector.sectionTitle)
  ) {
    return false;
  }

  if (selector.type && question.type !== selector.type) {
    return false;
  }

  if (selector.identifier && question.identifier !== selector.identifier) {
    return false;
  }

  if (selector.radioGroup && question.radioGroup !== selector.radioGroup) {
    return false;
  }

  if (selector.dbqName && question.dbqName !== selector.dbqName) {
    return false;
  }

  if (selector.textdbqName && question.textdbqName !== selector.textdbqName) {
    return false;
  }

  if (selector.question && question.question !== selector.question) {
    return false;
  }

  return true;
}

function findMatchingQuestions(questions, selector) {
  const matchingQuestions = [];

  (questions || []).forEach((question) => {
    walkVisibleQuestionTree(question, (node, section) => {
      if (questionMatchesSelector(node, section, selector)) {
        matchingQuestions.push(node);
      }
    });
  });

  return matchingQuestions;
}

function getQuestionValues(question, answerField) {
  const value = getValueByPath(question, answerField || "answer");
  return normalizeList(value).map(normalizeValue).filter(Boolean);
}

function getSourceContext(questions, sources) {
  return normalizeList(sources).reduce(
    (context, source) => {
      const matchingQuestions = findMatchingQuestions(questions, source);
      const sourceValueGroups = matchingQuestions.map((question) =>
        getQuestionValues(question, source.answerField)
      );
      const sourceValues = sourceValueGroups.reduce(
        (values, questionValues) => values.concat(questionValues),
        []
      );

      return {
        answerCount:
          context.answerCount +
          sourceValueGroups.filter((questionValues) => questionValues.length > 0)
            .length,
        availableCount: context.availableCount + matchingQuestions.length,
        values: context.values.concat(sourceValues),
      };
    },
    {
      answerCount: 0,
      availableCount: 0,
      values: [],
    }
  );
}

function valueMatches(value, expectedValues) {
  return normalizeList(expectedValues)
    .map(normalizeValue)
    .filter(Boolean)
    .includes(normalizeValue(value));
}

function getExpectedTargetAnswer(sourceContext, rules) {
  for (let rule of normalizeList(rules)) {
    if (
      rule.when === "anySourceEquals" &&
      sourceContext.values.some((value) =>
        valueMatches(value, rule.sourceAnswer)
      )
    ) {
      return normalizeValue(rule.targetAnswer);
    }

    if (
      rule.when === "allSourcesEqual" &&
      sourceContext.availableCount > 0 &&
      sourceContext.answerCount === sourceContext.availableCount &&
      sourceContext.values.every((value) =>
        valueMatches(value, rule.sourceAnswer)
      )
    ) {
      return normalizeValue(rule.targetAnswer);
    }
  }

  return "";
}

function formatAnswerConsistencyValidationMessage(config, context) {
  if (config.message) {
    return config.message
      .replace("##sourceLabel##", config.sourceLabel || "the source question")
      .replace("##targetLabel##", config.targetLabel || "this question")
      .replace("##sourceAnswers##", context.sourceValues.join(", "))
      .replace("##expectedTargetAnswer##", context.expectedTargetAnswer)
      .replace("##targetAnswer##", context.targetAnswer);
  }

  const sourceLabel = config.sourceLabel || "the source question";
  const targetLabel = config.targetLabel || "this question";

  return `${targetLabel} must be consistent with ${sourceLabel}. Please update the inconsistent response before proceeding.`;
}

export function validateAnswerConsistency(questions, currentQuestion) {
  const config = currentQuestion && currentQuestion.answerConsistencyValidation;
  if (!config || !config.target || !config.sources || !config.rules) {
    return null;
  }

  const targetQuestion = findMatchingQuestions(questions, config.target)[0];
  const targetAnswer =
    targetQuestion &&
    getQuestionValues(targetQuestion, config.target.answerField)[0];

  if (!targetAnswer) {
    return null;
  }

  const sourceContext = getSourceContext(questions, config.sources);
  const expectedTargetAnswer = getExpectedTargetAnswer(
    sourceContext,
    config.rules
  );

  if (!expectedTargetAnswer || targetAnswer === expectedTargetAnswer) {
    return null;
  }

  return {
    expectedTargetAnswer,
    message: formatAnswerConsistencyValidationMessage(config, {
      expectedTargetAnswer,
      sourceValues: sourceContext.values,
      targetAnswer,
    }),
    sourceValues: sourceContext.values,
    targetAnswer,
  };
}
